import { useQuery, useMutation, useTransport, createConnectQueryKey } from '@connectrpc/connect-query'
import { createClient } from '@connectrpc/connect'
import { create } from '@bufbuild/protobuf'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { useMemo } from 'react'

import {
  listAttachments,
  createAttachment,
  deleteAttachment,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListAttachmentsRequestSchema, CreateAttachmentRequestSchema, GetAttachmentRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { CortadoService } from '@/gen/cortado/v1/cortado_pb'
import { organizationStore } from './organization.store'
import { isValidUUID } from '@/lib/uuid'
import { readFileAsBase64 } from '@/lib/file-utils'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'

export const EMAIL_ATTACHMENT_LIMITS = {
  MAX_FILES: 10,
  // Combined cap across uploaded + org-library attachments (CPRO-611).
  MAX_TOTAL_BYTES: 20 * 1024 * 1024, // 20 MB
  ACCEPT: {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
    'application/pdf': ['.pdf'],
    'text/plain': ['.txt'],
    'text/csv': ['.csv'],
    'application/zip': ['.zip'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  } as const,
} as const

export function useAttachments(options?: {
  parentType?: ParentTypeEnum
  parentId?: string
  limit?: number
  offset?: number
  enabled?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const hasValidParent = !!options?.parentType && isValidUUID(options?.parentId)

  const request = useMemo(() => {
    return create(ListAttachmentsRequestSchema, {
      organizationId: selectedOrganizationId || '',
      parentType: options?.parentType,
      parentId: options?.parentId,
      limit: options?.limit,
      offset: options?.offset,
    })
  }, [selectedOrganizationId, options?.parentType, options?.parentId, options?.limit, options?.offset])

  const query = useQuery(
    listAttachments,
    request,
    {
      enabled: !!selectedOrganizationId && (options?.enabled ?? true) && hasValidParent,
    }
  )

  return {
    attachments: query.data?.attachments || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

export function useCreateAttachment() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createAttachment,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listAttachments,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useDeleteAttachment() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    deleteAttachment,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listAttachments,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useUploadEmailAttachment(orgId: string, parentType: ParentTypeEnum, parentId: string) {
  const transport = useTransport()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    createAttachment,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listAttachments,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  const upload = async (file: File): Promise<{ id: string; filename: string; sizeBytes: bigint }> => {
    const fileBase64 = await readFileAsBase64(file)
    const request = create(CreateAttachmentRequestSchema, {
      organizationId: orgId,
      parentType,
      parentId,
      filename: file.name,
      contentType: file.type,
      fileBase64,
    })
    const response = await mutation.mutateAsync(request)
    const attachment = response.attachment
    if (!attachment) throw new Error('Upload failed: no attachment returned')
    return {
      id: attachment.id,
      filename: attachment.filename,
      sizeBytes: attachment.sizeBytes,
    }
  }

  return { upload, isUploading: mutation.isPending }
}

export function useGetAttachmentDownloadURL() {
  const transport = useTransport()

  const getDownloadURL = async (attachmentId: string): Promise<string> => {
    const client = createClient(CortadoService, transport)
    const response = await client.getAttachment(
      create(GetAttachmentRequestSchema, { id: attachmentId })
    )
    const url = response.attachment?.downloadUrl
    if (!url) throw new Error('No download URL returned')
    return url
  }

  return { getDownloadURL }
}
